import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

/**
 * Exactly three live regions for the whole application (DESIGN.md §6.6).
 * More than three and they start talking over each other.
 *
 *   polite  — draft saved, representative added/removed, allocation succeeded
 *   alert   — save failures and validation failures that do not move focus
 *   route   — route and wizard-step changes, atomic
 *
 * Rule of thumb enforced by convention, not code: if focus is being moved to
 * something a screen reader will read anyway (an error summary, a new
 * heading), do not also push the same string into a region.
 */
export type LiveChannel = 'polite' | 'alert' | 'route'

interface AnnounceContextValue {
  announce: (message: string, channel?: LiveChannel) => void
}

const AnnounceContext = createContext<AnnounceContextValue | null>(null)

export function useAnnounce(): (message: string, channel?: LiveChannel) => void {
  const value = useContext(AnnounceContext)
  return value?.announce ?? (() => {})
}

export function LiveRegionProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState('')
  const [alert, setAlert] = useState('')
  const [route, setRoute] = useState('')
  const timers = useRef<number[]>([])

  const announce = useCallback((message: string, channel: LiveChannel = 'polite') => {
    const setter = channel === 'alert' ? setAlert : channel === 'route' ? setRoute : setPolite
    // Blank first: repeating an identical string is otherwise silent, because
    // the DOM never changed.
    setter('')
    const id = window.setTimeout(() => setter(message), 60)
    timers.current.push(id)
  }, [])

  useEffect(() => {
    const pending = timers.current
    return () => pending.forEach((id) => window.clearTimeout(id))
  }, [])

  const value = useMemo(() => ({ announce }), [announce])

  return (
    <AnnounceContext.Provider value={value}>
      {children}
      <div id="lr-polite" role="status" aria-live="polite" className="u-visually-hidden">
        {polite}
      </div>
      <div id="lr-alert" role="alert" aria-live="assertive" className="u-visually-hidden">
        {alert}
      </div>
      <div id="lr-route" aria-live="polite" aria-atomic="true" className="u-visually-hidden">
        {route}
      </div>
    </AnnounceContext.Provider>
  )
}

/**
 * Throttles a channel so repeated identical announcements (autosave, most
 * obviously) do not chatter. DESIGN.md caps autosave at one every 30 seconds.
 */
export function useThrottledAnnounce(intervalMs: number) {
  const announce = useAnnounce()
  const last = useRef(0)
  return useCallback(
    (message: string, channel: LiveChannel = 'polite') => {
      const now = Date.now()
      if (now - last.current < intervalMs) return
      last.current = now
      announce(message, channel)
    },
    [announce, intervalMs],
  )
}
